import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router';
import { Link } from '#components/atoms/link/Link.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageCloser } from '#components/patterns/page-closer/PageCloser.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useUrlParam } from '#hooks/useUrlParam.ts';
import { isTagName } from '#schemas/tag/name.ts';
import { NotFound } from '#screens/404/NotFound.tsx';
import { fallbackRenderNotFound } from '#screens/errors/fallbackRenderNotFound.tsx';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { fromArray, Titled } from '#util/title.ts';
import { TooManyTagsModal } from '../add/TooManyTagsModal.tsx';
import { useSuspenseIsTagsLimitReached } from '../add/useSuspenseIsTagsLimitReached.ts';
import { DuplicateTagForm } from '../duplicate/DuplicateTagForm.tsx';
import { TagForm } from '../form/TagForm.tsx';
import { TagsPage } from '../tags-page/TagsPage.tsx';

export function AddTagPage() {
  const location = useAppLocation();
  const navigate = useNavigate();
  const { isTagsLimitReached, tagsLimit } = useSuspenseIsTagsLimitReached();

  // When set, prefill the form with a copy of this tag's config.  Only the key:value shape is
  // checked — a tag created outside this client may violate the stricter new-input name rules yet
  // still be duplicated, and unknown names 404 via the source fetch instead.
  const [duplicateFrom] = useUrlParam('duplicateFrom');
  const isDuplicating = duplicateFrom !== undefined;
  const sourceTagName = duplicateFrom !== undefined && isTagName(duplicateFrom) ? duplicateFrom : undefined;

  const pageName = isDuplicating ? 'Duplicate' : 'Add';

  function returnToList() {
    void navigate(location.state?.from ?? '/tags');
  }

  function onFormSaved() {
    // Return to the list view after saving the tag
    void navigate(location.state?.from ?? '/tags', { replace: true });
  }

  // A present-but-malformed duplicateFrom is a broken link, not a plain add
  if (isDuplicating && !sourceTagName) {
    return <NotFound />;
  }

  return (
    <Titled title={(title) => fromArray([pageName, title])}>
      {isTagsLimitReached ?
        <>
          <TagsPage />
          <TooManyTagsModal onClose={returnToList} tagsLimit={tagsLimit} />
        </>
      : <ErrorBoundary fallbackRender={fallbackRenderNotFound}>
          <MainLayout
            header={
              <PageHeader
                title={
                  <>
                    <Link to="/tags" className="a">
                      Tags
                    </Link>
                    {' / '}
                    {pageName}
                  </>
                }
              >
                <PageCloser fallbackTo="/tags" />
              </PageHeader>
            }
          >
            <Suspense fallback={<LoadingSpinner />}>
              {sourceTagName ?
                <DuplicateTagForm sourceTagName={sourceTagName} onSuccess={onFormSaved} />
              : <TagForm tagName={null} hostCount={null} onSuccess={onFormSaved} />}
            </Suspense>
          </MainLayout>
        </ErrorBoundary>
      }
    </Titled>
  );
}
